"use client"

import React, { useState, useEffect, useRef, useCallback, useContext } from "react"
import { io } from "socket.io-client"
import { SOCKET_URL } from "../config"
import { getCookie } from "../utilities/cookieHelper"
import { authorizeContext } from "../hooks/Authorize"
import {
  MessageSquare,
  Send,
  CheckCircle,
  User,
  ChevronRight,
  Menu,
  X,
  Smile,
  PanelLeft,
  PanelRight,
  MoreHorizontal,
  Inbox,
  Mail,
  MessageCircle,
  Info,
  Globe,
  Monitor,
  HardDrive,
  Type,
  Activity,
  Link,
  Calendar,
  LogIn,
  WifiOff,
  UserPlus,
} from "lucide-react"
import EmojiPicker from "emoji-picker-react"
import {
  Box,
  Paper,
  Typography,
  Avatar,
  TextField,
  IconButton,
  Badge,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Tooltip,
  useTheme,
  Tab,
  Tabs,
  CircularProgress,
  ListItemIcon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import InviteAgentForm from "../components/Agents/InviteAgentForm"
import { jwtDecode } from "jwt-decode"
import "./Styles.css"

// Enhanced TypingIndicator component with better visibility and styling
const TypingIndicator = ({ name }) => {
  const theme = useTheme()
  // console.log(`[TYPING_INDICATOR_RENDER] Rendering TypingIndicator for: ${name}. Is theme available? ${!!theme}`)

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        p: 1.5, // Inner padding for the content
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: alpha(theme.palette.primary.main, 0.15),
          color: theme.palette.primary.main,
          mr: 1.5,
          fontSize: "0.75rem",
          fontWeight: 600,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        }}
      >
        {name && name.length > 0 ? name.charAt(0).toUpperCase() : "G"}
      </Avatar>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 500,
            fontSize: "0.7rem",
          }}
        >
          {name || "Guest"} is typing...
        </Typography>

        <Box sx={{ display: "flex", gap: "3px", alignItems: "center" }}>
          {[0, 0.2, 0.4].map((delay, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: theme.palette.primary.main,
                display: "inline-block",
                animation: `typingBounce 1.4s ${delay}s infinite ease-in-out`,
                boxShadow: `0 2px 4px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

const AgentDashboard = () => {
  const theme = useTheme() // Use the app's theme instead of hardcoded theme
  const [conversations, setConversations] = useState([])
  const [messages, setMessages] = useState({})
  const [socket, setSocket] = useState(null)
  const [activeConversation, setActiveConversation] = useState(null)
  const [isTyping, setIsTyping] = useState({})
  const [messageInput, setMessageInput] = useState("")
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [isAgentCurrentlyTyping, setIsAgentCurrentlyTyping] = useState(false)
  const [tabValue, setTabValue] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const messagesEndRef = useRef(null)
  const messageInputRef = useRef(null)
  const typingTimeoutRef = useRef({})
  const [guestOnlineStatus, setGuestOnlineStatus] = useState({})
  const [onlineWidgetIds, setOnlineWidgetIds] = useState(new Set())
  const [currentVisitorDetails, setCurrentVisitorDetails] = useState(null)
  const [isVisitorDetailsLoading, setIsVisitorDetailsLoading] = useState(false)
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [availableAgents, setAvailableAgents] = useState([])
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [isFetchingOrgMembers, setIsFetchingOrgMembers] = useState(false)
  const [isMessagesLoading, setIsMessagesLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [onlineAgentIds, setOnlineAgentIds] = useState(new Set())
  const [agentId, setAgentId] = useState(null)

  // Ref to hold the latest active conversation to avoid stale closures in socket listeners
  const activeConversationRef = useRef(activeConversation)

  // Update the ref whenever activeConversation state changes
  useEffect(() => {
    activeConversationRef.current = activeConversation
  }, [activeConversation])

  const authContextDirect = useContext(authorizeContext)
  const orgFromHook = authContextDirect?.currentOrganization
  const user = authContextDirect?.user
  const authToken = getCookie("accessToken")
  const apiUrl = process.env.REACT_APP_BACKEND_URL || "https://api.chat.clikkle.com"

  // console.log("AgentDashboard: orgFromHook from direct context access:", orgFromHook)
  // console.log("AgentDashboard: user from context:", user)

  useEffect(() => {
    // console.log("[AC_DEBUG] activeConversation state changed:", JSON.stringify(activeConversation))
    if (activeConversation) {
      // console.log("[AC_DEBUG] activeConversation.id:", activeConversation.id)
    } else {
      // console.log("[AC_DEBUG] activeConversation is null or undefined.")
    }
  }, [activeConversation])

  // Define fetchOrganizationMembers using useCallback
  const fetchOrganizationMembers = useCallback(
    async (orgId) => {
      if (!orgId) {
        setAvailableAgents([])
        return
      }

      setIsFetchingOrgMembers(true)
      const currentAuthToken = getCookie("accessToken")

      if (!currentAuthToken) {
        setAvailableAgents([])
        setIsFetchingOrgMembers(false)
        return
      }

      const ownerId = orgFromHook?.owner
      // console.log("AgentDashboard: Organization Owner ID:", ownerId)

      try {
        const fullEndpointUrl = `${apiUrl}/api/organization/${orgId}/members`
        const response = await fetch(fullEndpointUrl, {
          headers: {
            Authorization: `Bearer ${currentAuthToken}`,
            "Content-Type": "application/json",
          },
        })

        if (!response.ok) {
          throw new Error(`API error! status: ${response.status}`)
        }

        const data = await response.json()
        const membersArray = data.data && Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : []

        // console.log("AgentDashboard: Raw membersArray from API:", membersArray)

        const formattedMembers = membersArray
          .filter((member) => {
            return member._id !== ownerId
          })
          .map((member) => ({
            id: member._id,
            name: member.username || member.firstName || "Unknown Agent",
            email: member.email || "No email",
            role: member._id === ownerId ? "Admin" : "Agent",
          }))

        // console.log("AgentDashboard: Formatted members after filter/map:", formattedMembers)
        setAvailableAgents(formattedMembers)
      } catch (error) {
        setAvailableAgents([])
      } finally {
        setIsFetchingOrgMembers(false)
      }
    },
    [apiUrl, orgFromHook],
  )

  // Common emojis for quick access
  const commonEmojis = ["👋", "👍", "👎", "❤️", "😊", "😂", "🙏", "⚡", "🔥", "✅"]

  // Effect for initial data fetching and socket setup
  useEffect(() => {
    const currentAuthToken = getCookie("accessToken")
    const currentOrgData = authContextDirect?.currentOrganization
    const currentUser = authContextDirect?.user

    // console.log(
    //   "[INIT_LOAD_EFFECT] AgentDashboard: Main useEffect - Start. AuthToken:",
    //   !!currentAuthToken,
    //   "OrgData:",
    //   !!currentOrgData,
    //   "User:",
    //   !!currentUser,
    // )

    setIsLoading(true)

    // Fetch initial conversations
    if (currentAuthToken) {
      // console.log("AgentDashboard: Attempting to fetch conversations for organization:", currentOrgData?._id)
      fetch(`${apiUrl}/api/messages/conversations`, {
        headers: {
          Authorization: `Bearer ${currentAuthToken}`,
          // Explicitly send the organization ID from context
          "X-Organization-ID": currentOrgData?._id || "",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error fetching conversations! status: ${res.status}`)
          return res.json()
        })
        .then((data) => {
          // console.log("AgentDashboard: 📦 Received conversations raw data from API:", JSON.stringify(data, null, 2))
          const conversationsArray = Array.isArray(data) ? data : []
          const fetchedConversations = conversationsArray.map((conv) => {
            // console.log("AgentDashboard: 📦 Raw conv object inside map:", JSON.stringify(conv, null, 2))
            const mappedConv = {
              ...conv, // Spread existing properties
              // Ensure 'id' is consistently the conversationId string (e.g., "conv-...")
              // Use 'id' and 'dbId' directly as provided by the backend's conversation list API
              id: conv.id,
              dbId: conv.dbId,
            }
            // console.log("AgentDashboard: 📦 Mapped conv object inside map:", JSON.stringify(mappedConv, null, 2))
            return mappedConv
          })
          // console.log(
          //   "AgentDashboard: 📦 Mapped conversations for state (first item preview):",
          //   fetchedConversations.length > 0
          //     ? JSON.stringify(fetchedConversations[0], null, 2)
          //     : "No conversations to preview.",
          // )
          setConversations(fetchedConversations)
          // Removed auto-select from here. Now handled in a dedicated useEffect below.
          // if (fetchedConversations.length > 0 && !activeConversation) {
          //   selectConversation(fetchedConversations[0])
          // }
        })
        .catch((err) => {
          // console.error("AgentDashboard: ❌ Error loading conversations:", err)
          setConversations([])
        })
        .finally(() => setIsLoading(false))
    } else {
      // console.error("AgentDashboard: No auth token for fetching conversations.")
      setIsLoading(false)
      setConversations([])
    }

    // Fetch organization members if currentOrgData._id is available
    if (currentOrgData?._id) {
      // console.log("AgentDashboard: Org ID found, fetching members:", currentOrgData._id)
      fetchOrganizationMembers(currentOrgData._id)
    } else {
      // console.log("AgentDashboard: Org ID NOT found. Skipping member fetch.")
      setAvailableAgents([])
    }

    // Socket setup
    if (currentAuthToken && currentUser?._id) {
      // console.log(
      //   "AgentDashboard: 🔌 Initializing socket with User ID:",
      //   currentUser._id,
      //   "Organization:",
      //   currentOrgData?._id,
      // )
      const newSocket = io(SOCKET_URL, {
        transports: ["websocket"],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        auth: {
          token: currentAuthToken,
          userId: currentUser._id,
          organizationId: currentOrgData?._id,
        },
      })

      newSocket.on("connect", async () => {
        // console.log("AgentDashboard: ✅ Socket connected. ID:", newSocket.id)

        // Join organization rooms first
        if (currentOrgData?._id) {
          const orgRoom = `org-${currentOrgData._id}`
          const orgAdminRoom = `org-${currentOrgData._id}-admins`
          // console.log(`AgentDashboard: 🚪 Joining organization rooms:`, { orgRoom, orgAdminRoom })
          await Promise.all([
            new Promise((resolve) => {
              newSocket.emit("join-room", orgRoom, () => resolve())
            }),
            new Promise((resolve) => {
              newSocket.emit("join-room", orgAdminRoom, () => resolve())
            }),
          ])
          // console.log("AgentDashboard: ✅ Successfully joined organization rooms")
        }

        // Emit agent connect after joining rooms
        newSocket.emit("agent-connect", {
          userId: currentUser._id,
          organizationId: currentOrgData?._id,
        })

        // Join all existing conversation rooms (using conversations already fetched)
        for (const conv of conversations) {
          // Use the 'conversations' state directly
          if (conv.id) {
            // console.log(`AgentDashboard: 🚪 Joining conversation room: ${conv.id}`)
            await new Promise((resolve) => {
              newSocket.emit("join-conversation-room", conv.id, () => resolve())
            })
          }
        }
        // console.log("AgentDashboard: ✅ Successfully joined all conversation rooms")
        // No need to select first conversation here, as it's handled by the initial load logic
      })

      newSocket.on("connect_error", (error) => {
        // console.error("AgentDashboard: ❌ Socket connection error:", error)
      })

      newSocket.on("new-conversation", (data) => {
        // console.log("AgentDashboard: 📨 RAW data received by new-conversation handler:", JSON.stringify(data, null, 2)) // Add this line
        // console.log("AgentDashboard: 📨 New conversation received (raw data):", JSON.stringify(data, null, 2))
        setConversations((prev) => {
          const newConversation = {
            ...data,
            // Ensure id is correctly derived from potential backend fields
            id: data.id || data.conversationId || data._id,
            // Ensure dbId is correctly derived from potential backend fields
            dbId: data.dbId || data._id,
            unreadCount: 1,
          } // Mark new conversations as unread by default
          // console.log(
          //   "AgentDashboard: 📨 New conversation object created for state:",
          //   JSON.stringify(newConversation, null, 2),
          // )
          const updatedConversations = [newConversation, ...prev.filter((conv) => conv.id !== newConversation.id)]
          // Sort to bring new conversation to top, then by timestamp
          return updatedConversations.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        })

        // If no conversation is currently selected, select the new one
        if (!activeConversation) {
          // console.log("AgentDashboard: No active conversation, selecting new conversation:", data.id) // Use data.id
          selectConversation({
            ...data,
            id: data.id || data.conversationId || data._id, // Use data.id from backend
            dbId: data.dbId || data._id, // Use data.dbId from backend
          })
        }

        playNotificationSound()
      })

      newSocket.on("receive-message", (message) => {
        // console.log("[LIVE MSG] AgentDashboard: Raw 'receive-message' event. Message:", JSON.stringify(message))
        // console.log(
        //   "[LIVE MSG] AgentDashboard: Current activeConversation ID at receive-message event (from ref):",
        //   activeConversationRef.current?.id,
        // )
        // console.log("[LIVE MSG] AgentDashboard: Incoming message.conversationId:", message?.conversationId)

        if (!message || !message.conversationId) {
          // console.error("[LIVE MSG] AgentDashboard: Invalid message received (missing conversationId):", message)
          return
        }

        // console.log(
        //   "[LIVE MSG] AgentDashboard: Incoming message.conversationId:",
        //   message.conversationId,
        //   "Message Content:",
        //   message.content,
        //   "Message ClientID:",
        //   message.clientId,
        //   "Message _ID:",
        //   message._id,
        // )

        // Update messages state
        setMessages((prevMsgState) => {
          const currentMessagesForConv = prevMsgState[message.conversationId] || []
          // console.log(
          //   `[LIVE MSG] AgentDashboard: Messages in state for ${message.conversationId} BEFORE update:`,
          //   currentMessagesForConv.length,
          // )

          // Filter out any existing message with the same _id OR clientId
          // This ensures that optimistic messages (with clientId) are properly replaced by server-confirmed messages (with _id)
          const messagesWithoutIncomingDuplicate = currentMessagesForConv.filter((m) => {
            // Don't include if it has the same _id
            if (message._id && m._id === message._id) {
              return false;
            }
            // Don't include if it has the same clientId (for optimistic updates)
            if (message.clientId && m.clientId === message.clientId) {
              return false;
            }
            // Don't include if the incoming message has clientId and existing message has same content/timestamp (fallback)
            if (message.clientId && m.clientId && m.content === message.content && 
                Math.abs(new Date(m.timestamp).getTime() - new Date(message.timestamp).getTime()) < 2000) {
              return false;
            }
            return true;
          });
          
          // console.log(
          //   `[LIVE MSG] AgentDashboard: Filtered messages array length (removed ${currentMessagesForConv.length - messagesWithoutIncomingDuplicate.length} duplicates):`,
          //   messagesWithoutIncomingDuplicate.length,
          // )

          // Add the new incoming message (which now has a definitive _id from the server)
          const newMessagesArray = [...messagesWithoutIncomingDuplicate, message]

          // console.log(
          //   `[LIVE MSG] AgentDashboard: Messages for conv ${message.conversationId} AFTER update:`,
          //   newMessagesArray.map((m) => ({ content: m.content, id: m._id, clientId: m.clientId })),
          // )

          return {
            ...prevMsgState,
            [message.conversationId]: newMessagesArray,
          }
        })

        // Update conversations list (sidebar)
        setConversations((prevConvs) => {
          const updatedConvsList = prevConvs.map((conv) => {
            if (conv.id === message.conversationId) {
              // Get the conversation details from the message if available (sent by backend)
              const messageConv = message.conversation || {}
              return {
                ...conv,
                ...messageConv, // Merge conversation details from the message payload
                id: conv.id, // Keep the existing conversation ID from state
                dbId: messageConv._id || conv.dbId, // Ensure dbId is updated from the message's conversation _id
                lastMessage: message.content,
                timestamp: message.timestamp,
                // Increment unread count only if the conversation is NOT active AND the sender is a guest
                unreadCount:
                  activeConversationRef.current?.id !== message.conversationId && message.senderType === "guest"
                    ? (conv.unreadCount || 0) + 1
                    : conv.unreadCount || 0,
                guest: messageConv.guestIdentifier || conv.guest, // Update guest identifier
                status: messageConv.status || conv.status, // Update status
                widgetId: messageConv.widgetId || conv.widgetId, // Update widgetId
                assignedAgentId: messageConv.assignedAgentId || conv.assignedAgentId, // Update assigned agent
              }
            }
            return conv
          })

          // Sort conversations by most recent message (timestamp)
          const sortedConvs = updatedConvsList.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

          // console.log(
          //   "[LIVE MSG] AgentDashboard: Updated conversations list. New order:",
          //   sortedConvs.map((c) => ({
          //     id: c.id,
          //     lastMessage: c.lastMessage,
          //     timestamp: c.timestamp,
          //     unread: c.unreadCount,
          //   })),
          // )

          return sortedConvs
        })

        // Handle active conversation update and scrolling
        // console.log(
        //   `[LIVE MSG DEBUG] Comparison: activeConversationRef.current?.id (${activeConversationRef.current?.id}) === message.conversationId (${message.conversationId}) -> ${activeConversationRef.current?.id === message.conversationId}`,
        // )
        if (activeConversationRef.current?.id === message.conversationId) {
          scrollToBottom() // Scroll to bottom for the active conversation
        } else {
          // console.log(
          //   `[LIVE MSG] AgentDashboard: Message is for a NON-ACTIVE conversation. Active ID: ${activeConversationRef.current?.id}, Msg Conv ID: ${message.conversationId}. Playing sound.`,
          // )
          if (message.senderType !== "agent") {
            playNotificationSound() // Play sound for incoming guest messages to non-active conversations
          }
        }
      })

      newSocket.on("typing-start", (data) => {
        // console.log(
        //   `[TYPING_DEBUG_AGENT_DASH] Received typing-start. Data: ${JSON.stringify(data)}. Current activeConversation.id: ${activeConversationRef.current?.id}`,
        // )
        if (data.userType === "guest") {
          setIsTyping((prev) => {
            const newState = {
              ...prev,
              [data.conversationId]: { isTyping: true, name: data.userName || "Guest" },
            }
            // console.log(
            //   `[TYPING_DEBUG_AGENT_DASH] typing-start: Updated isTyping for ${data.conversationId}: ${JSON.stringify(newState[data.conversationId])}`,
            // )
            return newState
          })
        }
      })

      newSocket.on("typing-stop", (data) => {
        // console.log(
        //   `[TYPING_DEBUG_AGENT_DASH] Received typing-stop. Data: ${JSON.stringify(data)}. Current activeConversation.id: ${activeConversationRef.current?.id}`,
        // )
        if (data.userType === "guest") {
          setIsTyping((prev) => {
            const newState = {
              ...prev,
              [data.conversationId]: { ...(prev[data.conversationId] || {}), isTyping: false },
            }
            // console.log(
            //   `[TYPING_DEBUG_AGENT_DASH] typing-stop: Updated isTyping for ${data.conversationId}: ${JSON.stringify(newState[data.conversationId])}`,
            // )
            return newState
          })
        }
      })

      newSocket.on("online-widgets-status", (data) => {
        // console.log("[SOCKET EVENT] AgentDashboard: Received 'online-widgets-status' data:", JSON.stringify(data))
        setGuestOnlineStatus((prevStatus) => {
          // console.log(
          //   "[SOCKET EVENT] AgentDashboard: Updating guestOnlineStatus from 'online-widgets-status'. Previous status:",
          //   JSON.stringify(prevStatus),
          //   "New data:",
          //   JSON.stringify(data),
          // )
          // Ensure data is an object before attempting to update
          if (data && typeof data === "object") {
            return data
          } else {
            // console.warn(
            //   "[SOCKET EVENT] AgentDashboard: Received invalid or empty 'online-widgets-status' data. Not updating guestOnlineStatus. Data:",
            //   data,
            // )
            return prevStatus
          }
        })

        const currentOnlineWidgetIds = data ? Object.keys(data).filter((widgetId) => data[widgetId] === true) : []
        setOnlineWidgetIds(new Set(currentOnlineWidgetIds))
      })

      newSocket.on("online-agents-status", (agentUserIds) => {
        // console.log("AgentDashboard: Received 'online-agents-status'", agentUserIds)
        if (Array.isArray(agentUserIds)) {
          setOnlineAgentIds(new Set(agentUserIds))
        } else {
          setOnlineAgentIds(new Set())
        }
      })

      setSocket(newSocket)
      setAgentId(currentUser._id)

      return () => {
        // console.log("AgentDashboard: 🧹 Cleaning up socket connection")
        newSocket.off("online-agents-status")
        newSocket.disconnect()
      }
    } else {
      // console.log("AgentDashboard: Socket not initialized (missing token or user data).")
      setSocket(null)
    }
  }, [authContextDirect?.currentOrganization, authContextDirect?.user, fetchOrganizationMembers, apiUrl])

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom()
  }, [messages, activeConversation])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const playNotificationSound = () => {
    const audio = new Audio("/notification.mp3")
    audio.play().catch((e) => console.log("Audio play failed:", e))
  }

  const handleAssignConversation = (conversationId) => {
    if (!socket) return

    socket.emit("assign-agent", {
      conversationId,
      agentId: agentId,
    })

    setConversations((prev) =>
      prev.map((conv) => (conv.id === conversationId ? { ...conv, status: "assigned" } : conv)),
    )
  }

  const handleResolveConversation = (conversationId) => {
    if (!socket) return

    socket.emit("resolve-conversation", {
      conversationId,
    })

    setConversations((prev) =>
      prev.map((conv) => (conv.id === conversationId ? { ...conv, status: "resolved" } : conv)),
    )
  }

  const fetchVisitorDetails = useCallback(
    async (widgetId) => {
      // Only attempt to fetch if widgetId is valid
      if (!widgetId) {
        // console.log("AgentDashboard: Skipping visitor details fetch, widgetId is missing.")
        setCurrentVisitorDetails(null)
        setIsVisitorDetailsLoading(false)
        return
      }

      setIsVisitorDetailsLoading(true)
      try {
        const currentApiUrl = process.env.REACT_APP_BACKEND_URL || "https://api.chat.clikkle.com"
        const currentAuthToken = getCookie("accessToken")
        const response = await fetch(`${currentApiUrl}/api/visitor-details/${widgetId}`, {
          headers: {
            Authorization: `Bearer ${currentAuthToken}`,
          },
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        setCurrentVisitorDetails(data.visitor || null)
      } catch (error) {
        console.error("Failed to fetch visitor details:", error)
        setCurrentVisitorDetails(null)
      } finally {
        setIsVisitorDetailsLoading(false)
      }
    },
    [getCookie, setIsVisitorDetailsLoading, setCurrentVisitorDetails], // Dependencies for fetchVisitorDetails
  )

  const selectConversation = useCallback(
    async (conversation) => {
      // console.log(
      //   "[CONV_SELECT] AgentDashboard: Function invoked. Raw conversation object received:",
      //   JSON.stringify(conversation, null, 2),
      // )
      // console.log("[CONV_SELECT] AgentDashboard: conversation.id property received:", conversation?.id)
      // console.log(
      //   "[CONV_SELECT] AgentDashboard: conversation.conversationId property received:",
      //   conversation?.conversationId,
      // )
      // console.log("[CONV_SELECT] AgentDashboard: conversation._id property received:", conversation?._id)

      // Create a safe, normalized conversation object to set as activeConversation
      const normalizedConversation = {
        ...conversation,
        // Ensure 'id' is consistently the conversationId string (e.g., "conv-...")
        // Use conversation.id or conversation.conversationId or conversation._id as fallbacks
        id: conversation.id || conversation.conversationId || conversation._id,
        dbId: conversation.dbId || conversation._id,
      }

      // Explicitly reset typing state for this conversation when it becomes active
      setIsTyping((prev) => ({
        ...prev,
        [normalizedConversation.id]: { isTyping: false, name: '' } // Ensure it starts as not typing
      }))
      // console.log(`[CONV_SELECT] AgentDashboard: Resetting typing state for ${normalizedConversation.id}`)

      // console.trace("[CONV_SELECT] Trace before setActiveConversation")
      // console.log(
      //   "[CONV_SELECT] AgentDashboard: Setting activeConversation to:",
      //   JSON.stringify(normalizedConversation, null, 2),
      // )
      setActiveConversation(normalizedConversation)
      // console.log(
      //   "[CONV_SELECT] AgentDashboard: activeConversation state SET. Now logging its ID after set:",
      //   normalizedConversation?.id,
      // )
      // console.log(
      //   "[CONV_SELECT] AgentDashboard: activeConversation state SET. Full object after set:",
      //   JSON.stringify(normalizedConversation, null, 2),
      // )

      setIsMessagesLoading(true)
      setSelectedAgent(null)

      // Reset unread count for the selected conversation
      setConversations((prevConvs) =>
        prevConvs.map(
          (conv) => (conv.id === normalizedConversation.id ? { ...conv, unreadCount: 0 } : conv), // Use normalizedConversation.id
        ),
      )

      if (socket && normalizedConversation?.id) {
        // Use normalizedConversation.id
        // console.log("[CONV_SELECT] AgentDashboard: Emitting join-conversation-room for:", normalizedConversation.id)
        socket.emit("join-conversation-room", normalizedConversation.id) // Use normalizedConversation.id

        // Also join the organization's admin room if not already joined
        if (authContextDirect?.currentOrganization?._id) {
          const orgAdminRoom = `org-${authContextDirect.currentOrganization._id}-admins`
          // console.log(`[CONV_SELECT] AgentDashboard: Joining admin room: ${orgAdminRoom}`)
          socket.emit("join-room", orgAdminRoom)
        }
      }

      // Removed: Fetch messages for the selected conversation. This is now handled by the useEffect below.
      // Removed: Fetch visitor details from here. It will now be handled by a dedicated useEffect.
      // Removed: Message fetching logic (moved to useEffect)

      setIsMessagesLoading(false)
    },
    [apiUrl, getCookie, socket, fetchVisitorDetails], // Dependencies for fetchVisitorDetails
  )

  // Effect for fetching visitor details when active conversation changes
  useEffect(() => {
    if (activeConversation && activeConversation.widgetId) {
      // console.log(
      //   "[FETCH_VISITOR_EFFECT] Active conversation changed, fetching visitor details for widgetId:",
      //   activeConversation.widgetId,
      // )
      fetchVisitorDetails(activeConversation.widgetId)
    } else {
      // console.log(
      //   "[FETCH_VISITOR_EFFECT] No active conversation or activeConversation.widgetId is missing. Skipping visitor details fetch.",
      // )
      setCurrentVisitorDetails(null) // Clear visitor details if no valid widgetId
    }
  }, [activeConversation, fetchVisitorDetails])

  const handleSendMessage = (e) => {
    e.preventDefault()
    if (!messageInput.trim() || !activeConversation) return

    if (activeConversation) {
      const conversationTypingTimeout = typingTimeoutRef.current[activeConversation.id]
      if (conversationTypingTimeout) {
        clearTimeout(conversationTypingTimeout)
      }
      if (isAgentCurrentlyTyping) {
        sendAgentTypingEvent(false)
        setIsAgentCurrentlyTyping(false)
      }
    }

    // console.log(
    //   "AgentDashboard: 📨 Sending message:",
    //   messageInput.trim(),
    //   "to guest via widgetId",
    //   activeConversation.widgetId,
    //   "in conversation",
    //   activeConversation.id,
    // )

    if (!activeConversation.widgetId) {
      // console.error("AgentDashboard: ❌ Cannot send message. activeConversation.widgetId is missing.")
      return
    }

    const timestamp = new Date().toISOString()
    const tempClientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`

    const messageData = {
      from: user._id,
      to: activeConversation.guest || `guest-${activeConversation.widgetId}`,
      content: messageInput.trim(),
      timestamp: timestamp,
      conversationId: activeConversation.id,
      widgetId: activeConversation.widgetId,
      targetWidgetId: activeConversation.widgetId,
      senderType: "agent",
      clientId: tempClientId,
      status: "sending", // Add a status for optimistic UI
    }

    // console.log("AgentDashboard: 📤 Emitting send-message with data:", messageData)

    setMessages((prev) => ({
      ...prev,
      [activeConversation.id]: [...(prev[activeConversation.id] || []), messageData],
    }))

    scrollToBottom() // Scroll to bottom immediately after optimistic update

    socket.emit("send-message", messageData)
    setMessageInput("")
    setShowEmojiPicker(false)
  }

  const sendAgentTypingEvent = useCallback(
    (isTypingNow) => {
      if (!socket || !activeConversation) {
        return
      }

      const eventName = isTypingNow ? "typing-start" : "typing-stop"
      const payload = {
        conversationId: activeConversation.id,
        userType: "agent",
        userName: "Agent",
      }

      socket.emit(eventName, payload)
    },
    [socket, activeConversation],
  )

  const addEmoji = (emojiData) => {
    setMessageInput((prev) => prev + emojiData.emoji)
    messageInputRef.current?.focus()
  }

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => !prev)
  }

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const formatDate = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleDateString()
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case "assigned":
        return <User className="w-4 h-4 text-blue-500" />
      case "resolved":
        return <CheckCircle className="w-4 h-4 text-green-500" />
      default:
        return <MoreHorizontal className="w-4 h-4 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "assigned":
        return { bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.main }
      case "resolved":
        return { bgcolor: alpha(theme.palette.secondary.main, 0.2), color: theme.palette.secondary.main }
      default:
        return { bgcolor: alpha(theme.palette.grey[500], 0.2), color: theme.palette.grey[500] }
    }
  }

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev)
  }

  function generateConversationId() {
    return "conv-" + Date.now() + "-" + Math.random().toString(36).substr(2, 9)
  }

  useEffect(() => {
    if (!messageInput && isAgentCurrentlyTyping && activeConversation) {
      const currentConvId = activeConversation.id
      const conversationTypingTimeout = typingTimeoutRef.current[currentConvId]
      if (conversationTypingTimeout) {
        clearTimeout(conversationTypingTimeout)
        typingTimeoutRef.current[currentConvId] = null
      }
      if (isAgentCurrentlyTyping) {
        sendAgentTypingEvent(false)
        setIsAgentCurrentlyTyping(false)
      }
    }
  }, [messageInput, isAgentCurrentlyTyping, activeConversation, sendAgentTypingEvent])

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const filteredConversations = conversations.filter((conv) => {
    if (tabValue === 0) return true
    if (tabValue === 1) return conv.unreadCount > 0
    if (tabValue === 2) return !conv.unreadCount
    return true
  })

  const unreadCount = conversations.reduce((total, conv) => total + (conv.unreadCount || 0), 0)

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true)
  }

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false)
  }

  const handleAgentSelection = async (agentToAssign) => {
    if (!agentToAssign || !agentToAssign.id) {
      // console.error("AgentDashboard: Invalid agent object passed to handleAgentSelection.")
      return
    }

    const previousSelectedAgent = selectedAgent
    setSelectedAgent(agentToAssign)

    if (!activeConversation || (!activeConversation.id && !activeConversation._id)) {
      // console.error("AgentDashboard: No active conversation to assign agent to.")
      setSelectedAgent(previousSelectedAgent)
      alert("Please select a conversation first.")
      return
    }

    const conversationBackendId = activeConversation.dbId || activeConversation._id || activeConversation.id

    if (!conversationBackendId) {
      // console.error("AgentDashboard: Cannot assign agent. Backend ID for the conversation is missing.")
      setSelectedAgent(previousSelectedAgent)
      alert("Could not assign agent: Conversation identifier missing.")
      return
    }

    // console.log(
    //   `AgentDashboard: Assigning agent ${agentToAssign.name} (ID: ${agentToAssign.id}) to conversation (Backend ID: ${conversationBackendId})`,
    // )

    const orgToken = getCookie("orgToken")
    let organizationId = null

    if (orgToken) {
      try {
        const decoded = jwtDecode(orgToken)
        organizationId = decoded.organizationId
      } catch (error) {
        // console.error("AgentDashboard: Error decoding orgToken for agent assignment:", error)
        setSelectedAgent(previousSelectedAgent)
        alert("Error assigning agent: Could not verify organization.")
        return
      }
    }

    if (!organizationId) {
      // console.error("AgentDashboard: No organization ID found in orgToken for agent assignment")
      setSelectedAgent(previousSelectedAgent)
      alert("Error assigning agent: Organization ID missing.")
      return
    }

    try {
      const response = await fetch(`${apiUrl}/api/conversations/${conversationBackendId}/assign`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
          "X-Organization-ID": organizationId,
        },
        body: JSON.stringify({ agentId: agentToAssign.id }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Failed to parse error response from server" }))
        // console.error("AgentDashboard: API error assigning agent:", errorData)
        throw new Error(errorData.error || `Failed to assign agent: ${response.status}`)
      }

      const updatedConversationFromServer = await response.json()
      // console.log(
      //   "AgentDashboard: Agent assigned successfully via API. Server response:",
      //   updatedConversationFromServer,
      // )

      const newAssignedAgentId = updatedConversationFromServer.data?.assignedAgentId

      setActiveConversation((prevActive) => ({
        ...prevActive,
        assignedAgentId: newAssignedAgentId,
      }))

      setConversations((prevConvs) =>
        prevConvs.map((conv) =>
          (conv.dbId || conv._id || conv.id) === conversationBackendId
            ? { ...conv, assignedAgentId: newAssignedAgentId }
            : conv,
        ),
      )
    } catch (error) {
      // console.error("AgentDashboard: Catch block - Error assigning agent:", error.message)
      setSelectedAgent(previousSelectedAgent)
      alert(`Error assigning agent: ${error.message}`)
    }
  }

  useEffect(() => {
    // console.log(
    //   "AgentDashboard: useEffect for setting selectedAgent. ActiveConv AssignedAgentId:",
    //   activeConversation?.assignedAgentId,
    //   "Available Agents Count:",
    //   availableAgents.length,
    //   "Active Conv:",
    //   activeConversation,
    // )

    if (activeConversation && activeConversation.assignedAgentId && availableAgents.length > 0) {
      let agentIdToFind = null

      if (
        typeof activeConversation.assignedAgentId === "object" &&
        activeConversation.assignedAgentId !== null &&
        activeConversation.assignedAgentId._id
      ) {
        agentIdToFind = activeConversation.assignedAgentId._id
      } else if (typeof activeConversation.assignedAgentId === "string") {
        agentIdToFind = activeConversation.assignedAgentId
      }

      if (agentIdToFind) {
        const agent = availableAgents.find((a) => a.id === agentIdToFind)
        // console.log("AgentDashboard: Attempting to select agent. ID to find:", agentIdToFind, "Found agent:", agent)
        setSelectedAgent(agent || null)
      } else {
        // console.log(
        //   "AgentDashboard: assignedAgentId on activeConversation is not in a recognized format or agentIdToFind is null. Setting selectedAgent to null. assignedAgentId:",
        //   activeConversation.assignedAgentId,
        // )
        setSelectedAgent(null)
      }
    } else if (activeConversation && !activeConversation.assignedAgentId) {
      // console.log(
      //   "AgentDashboard: Active conversation is unassigned (no assignedAgentId field or it's null/undefined). Setting selectedAgent to null.",
      // )
      setSelectedAgent(null)
    } else if (!activeConversation) {
      // console.log("AgentDashboard: No active conversation. Setting selectedAgent to null.")
      setSelectedAgent(null)
    }
  }, [activeConversation, availableAgents])

  useEffect(() => {
    if (!socket) return

    socket.on("conversation-assigned", (data) => {
      // console.log("AgentDashboard: 📨 Received conversation assignment:", data)
      fetch(`${apiUrl}/api/messages/conversations`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)
          return res.json()
        })
        .then((data) => {
          // console.log("AgentDashboard: 📦 Refreshed conversations after assignment:", data)
          setConversations(Array.isArray(data) ? data : [])
        })
        .catch((err) => {
          // console.error("AgentDashboard: ❌ Error refreshing conversations:", err)
        })
    })

    socket.on("agent-assigned", (data) => {
      // console.log("AgentDashboard: 📨 Agent assigned to conversation:", data)
      setConversations((prevConvs) =>
        prevConvs.map((conv) =>
          conv.id === data.conversationId ? { ...conv, assignedAgentId: data.agentId, status: "assigned" } : conv,
        ),
      )

      setActiveConversation((prevActive) =>
        prevActive?.id === data.conversationId
          ? { ...prevActive, assignedAgentId: data.agentId, status: "assigned" }
          : prevActive,
      )
    })

    return () => {
      socket.off("conversation-assigned")
      socket.off("agent-assigned")
    }
  }, [socket, authToken, apiUrl])

  useEffect(() => {
    if (activeConversation && activeConversation.id) {
      const conversationIdToFetch = activeConversation.id
      // console.log(
      //   "[FETCH_MESSAGES_EFFECT] Active conversation changed, fetching messages for:",
      //   conversationIdToFetch,
      //   "Full activeConv:",
      //   JSON.stringify(activeConversation),
      // )

      const currentAuthToken = getCookie("accessToken")
      const messagesUrl = `${process.env.REACT_APP_BACKEND_URL || "https://api.chat.clikkle.com"}/api/messages/${conversationIdToFetch}`
      // console.log("[FETCH_MESSAGES_EFFECT] Fetching messages from URL:", messagesUrl)

      setIsMessagesLoading(true)

      fetch(messagesUrl, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentAuthToken}`,
        },
      })
        .then((res) => {
          // console.log("[FETCH_MESSAGES_EFFECT] Raw response from server for", conversationIdToFetch, ":", res)
          if (!res.ok) {
            // console.error(
            //   "[FETCH_MESSAGES_EFFECT] Network response was not ok. Status:",
            //   res.status,
            //   "StatusText:",
            //   res.statusText,
            // )
            return res
              .json()
              .then((errData) => {
                // console.error("[FETCH_MESSAGES_EFFECT] Error data from server:", errData)
                throw new Error(
                  `Server error: ${res.status} ${res.statusText} - ${errData.message || "No specific error message"}`,
                )
              })
              .catch(() => {
                throw new Error(`Server error: ${res.status} ${res.statusText} - Response not valid JSON or empty.`)
              })
          }
          return res.json()
        })
        .then((data) => {
          // console.log(
          //   "[FETCH_MESSAGES_EFFECT] ✅ Received messages data from server for",
          //   conversationIdToFetch,
          //   ":",
          //   data,
          // )
          if (data && data.success && Array.isArray(data.messages)) {
            setMessages((prevMessages) => ({
              ...prevMessages,
              [conversationIdToFetch]: data.messages,
            }))
            // console.log("[FETCH_MESSAGES_EFFECT] Updated messages state for", conversationIdToFetch)

            if (data.conversation) {
              setConversations((prevConvs) =>
                prevConvs.map((c) =>
                  c.id === data.conversation.conversationId
                    ? {
                        ...c,
                        ...data.conversation,
                        guestIdentifier: data.conversation.guestIdentifier || c.guestIdentifier,
                      }
                    : c,
                ),
              )
              // console.log(
              //   "[FETCH_MESSAGES_EFFECT] Updated conversations list with details from fetched conversation data for",
              //   data.conversation.conversationId,
              // )
            }
          } else {
            // console.warn(
            //   "[FETCH_MESSAGES_EFFECT] Received data for",
            //   conversationIdToFetch,
            //   ", but it's not in the expected format or success is false. Data:",
            //   data,
            // )
            setMessages((prevMessages) => ({
              ...prevMessages,
              [conversationIdToFetch]: [],
            }))
          }
        })
        .catch((error) => {
          // console.error("[FETCH_MESSAGES_EFFECT] ❌ Error fetching messages for", conversationIdToFetch, ":", error)
          setMessages((prevMessages) => ({
            ...prevMessages,
            [conversationIdToFetch]: [],
          }))
        })
        .finally(() => {
          setIsMessagesLoading(false)
          // console.log("[FETCH_MESSAGES_EFFECT] Finished fetching messages for", conversationIdToFetch, ".")
        })
    } else {
      // console.log(
      //   "[FETCH_MESSAGES_EFFECT] No active conversation or activeConversation.id is missing. No fetch performed.",
      // )
    }
  }, [activeConversation, getCookie, setMessages, setConversations, setIsMessagesLoading])

  // console.log("jjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjjj", currentVisitorDetails)

  // Add this after all useEffects and before the return statement
  useEffect(() => {
    if (socket && conversations.length > 0 && !activeConversation) {
      selectConversation(conversations[0])
    }
  }, [socket, conversations, activeConversation, selectConversation])

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default", // Use theme background
      }}
    >
      {/* Mobile menu button */}
      <IconButton
        onClick={toggleSidebar}
        sx={{
          display: { xs: "flex", md: "none" },
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: 1300,
          bgcolor: "background.paper", // Use theme background
          boxShadow: 2,
          "&:hover": { bgcolor: alpha(theme.palette.background.paper, 0.8) },
          transition: "all 0.2s",
        }}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </IconButton>

      {/* Conversations List */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={sidebarOpen}
        sx={{
          width: { xs: "100%", md: 320, lg: 380 },
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: { xs: "100%", md: 320, lg: 380 },
            boxSizing: "border-box",
            position: { xs: "fixed", md: "relative" },
            height: "100%",
            zIndex: { xs: 1200, md: "auto" },
            transition: "transform 0.3s ease-in-out",
            transform: {
              xs: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
              md: "translateX(0)",
            },
            bgcolor: "background.default", // Use theme background
            borderRight: `1px solid ${theme.palette.divider}`, // Use theme divider
          },
        }}
      >
        <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <MessageSquare size={20} color={theme.palette.primary.main} />
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                Conversations
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton onClick={toggleSidebar} sx={{ display: { xs: "flex", md: "none" } }}>
              <ChevronRight size={20} />
            </IconButton>
          </Toolbar>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                minHeight: 48,
                py: 1,
              },
            }}
          >
            <Tab icon={<MessageCircle size={18} />} iconPosition="start" label="All" />
            <Tab
              icon={
                <Badge badgeContent={unreadCount} color="primary" max={99}>
                  <Inbox size={18} />
                </Badge>
              }
              iconPosition="start"
              label="Unread"
            />
            <Tab icon={<Mail size={18} />} iconPosition="start" label="Read" />
          </Tabs>
        </AppBar>
        <Box sx={{ overflow: "auto", height: "calc(100vh - 112px)" }}>
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : filteredConversations.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 256,
                color: "text.secondary",
              }}
            >
              <MessageSquare size={48} color={theme.palette.grey[700]} style={{ marginBottom: 8 }} />
              <Typography variant="body1">
                No conversations {tabValue === 1 ? "unread" : tabValue === 2 ? "read" : ""}
              </Typography>
              <Typography variant="body2">
                {tabValue === 0
                  ? "New chats will appear here"
                  : tabValue === 1
                    ? "Unread messages will appear here"
                    : "Read conversations will appear here"}
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {filteredConversations.map((conv) => (
                <ListItem
                  key={conv.id}
                  button
                  component="button" // Added to fix `button` prop warning
                  divider
                  onClick={() => {
                    selectConversation(conv)
                    if (window.innerWidth < 768) {
                      setSidebarOpen(false)
                    }
                  }}
                  sx={{
                    p: 2,
                    transition: "all 0.15s ease",
                    bgcolor:
                      activeConversation?.id === conv.id
                        ? alpha(theme.palette.primary.main, 0.15)
                        : conv.unreadCount
                          ? alpha(theme.palette.primary.main, 0.05)
                          : "transparent",
                    borderLeft: activeConversation?.id === conv.id ? 4 : 0,
                    borderLeftColor: "primary.main",
                    "&:hover": {
                      bgcolor:
                        activeConversation?.id === conv.id
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha(theme.palette.action.hover, 0.5),
                    },
                    animation: conv.unreadCount ? "pulse 2s infinite" : "none",
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={conv.unreadCount || 0}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          animation: conv.unreadCount ? "pulse 2s infinite" : "none",
                        },
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                          color: theme.palette.primary.main,
                        }}
                      >
                        {(conv.guest || "G").charAt(0).toUpperCase()}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" component="span" noWrap sx={{ fontWeight: 500 }}>
                        {conv.guest}
                      </Typography>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          component="span" // Ensure this is a span to avoid nesting p in p
                        >
                          {conv.lastMessage}
                        </Typography>
                        <Box
                          component="div" // Ensure this is a div to avoid nesting div in p
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 0.5,
                            fontSize: "0.75rem",
                            color: "text.disabled",
                          }}
                        >
                          <Typography variant="caption" component="span">
                            {formatTime(conv.timestamp)}
                          </Typography>
                          {(() => {
                            const messageDate = new Date(conv.timestamp)
                            const today = new Date()
                            if (
                              messageDate.getFullYear() !== today.getFullYear() ||
                              messageDate.getMonth() !== today.getMonth() ||
                              messageDate.getDate() !== today.getDate()
                            ) {
                              return (
                                <React.Fragment>
                                  <Box component="span" sx={{ mx: 0.5 }}>
                                    •
                                  </Box>
                                  <Typography variant="caption" component="span">
                                    {formatDate(conv.timestamp)}
                                  </Typography>
                                </React.Fragment>
                              )
                            }
                            return null
                          })()}
                        </Box>
                      </React.Fragment>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Chat Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          ml: { xs: 0, md: sidebarOpen ? 0 : -40 },
          transition: "margin-left 0.3s ease-in-out",
          bgcolor: "background.default", // Use theme background
        }}
      >
        {/* Toggle sidebar button for tablet/desktop */}
        <IconButton
          onClick={toggleSidebar}
          sx={{
            display: { xs: "none", md: "flex" },
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 10,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            boxShadow: 1,
            "&:hover": { bgcolor: theme.palette.background.paper },
          }}
        >
          {sidebarOpen ? <PanelLeft size={16} /> : <PanelRight size={16} />}
        </IconButton>

        {activeConversation ? (
          <>
            <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Toolbar>
                <Box sx={{ display: "flex", alignItems: "center" }}>

                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      mr: 2,
                      ml: 4,
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: theme.palette.primary.main,
                    }}
                  >
                    {(activeConversation?.guest || "G").charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {activeConversation?.guest || "Guest"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Widget: {activeConversation.widgetId}
                      </Typography>
                      {activeConversation.widgetId && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: guestOnlineStatus[activeConversation.widgetId]
                                ? theme.palette.success?.main || "#4caf50"
                                : theme.palette.grey[500],
                              display: "inline-block",
                              ml: 0.5,
                            }}
                          />
                          <Typography
                            variant="caption"
                            color={guestOnlineStatus[activeConversation.widgetId] ? "success.main" : "text.secondary"}
                          >
                            {guestOnlineStatus[activeConversation.widgetId] ? "Online" : "Offline"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
              </Toolbar>
            </AppBar>
            <Box
              sx={{
                flexGrow: 1,
                overflow: "auto",
                p: 2,
                bgcolor: "background.default", // Use theme background
                display: "flex",
                flexDirection: "column",
                position: "relative", // Added for proper positioning context
              }}
            >
              {isMessagesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                  <CircularProgress />
                </Box>
              ) : (
                (() => {
                  if (!activeConversation) {
                    // console.log("[RENDER_DEBUG] No activeConversation, so not rendering messages.")
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "text.secondary",
                        }}
                      >
                        <MessageSquare size={48} color={theme.palette.grey[700]} style={{ marginBottom: 8 }} />
                        <Typography variant="body1">No messages yet</Typography>
                        <Typography variant="body2">Start the conversation</Typography>
                      </Box>
                    )
                  }
                  const currentActiveConvId = activeConversation.id
                  // console.log(
                  //   "[RENDER_DEBUG] Attempting to render messages. activeConversation.id:",
                  //   currentActiveConvId,
                  // )
                  // console.log(
                  //   "[RENDER_DEBUG] Full activeConversation object:",
                  //   JSON.stringify(activeConversation, null, 2),
                  // )
                  // console.log("[RENDER_DEBUG] All messages state keys:", Object.keys(messages))
                  const messagesForActiveConv = messages[currentActiveConvId]
                  // console.log(
                  //   "[RENDER_DEBUG] Messages for active conv from state (messages[",
                  //   currentActiveConvId,
                  //   "]):",
                  // )
                  // console.log(
                  //   messagesForActiveConv
                  //     ? `${messagesForActiveConv.length} messages found.`
                  //     : "NOT FOUND OR EMPTY array.",
                  // )
                  if (messagesForActiveConv && messagesForActiveConv.length > 0) {
                    // console.log(
                    //   "[RENDER_DEBUG] Rendering message list for",
                    //   currentActiveConvId,
                    //   ". Message count:",
                    //   messagesForActiveConv.length,
                    // )
                    return messagesForActiveConv.map((message, index) => {
                      const isAgent = message.from === user?._id || message.senderType === "agent"
                      return (
                        <Box
                          key={message._id || message.clientId || message.timestamp || index}
                          sx={{
                            mb: 2,
                            alignSelf: isAgent ? "flex-end" : "flex-start",
                            maxWidth: "70%",
                            animation: "fadeIn 0.3s ease-out forwards",
                            animationDelay: `${index * 0.05}s`,
                          }}
                        >
                          <Paper
                            elevation={1}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              bgcolor: isAgent ? "primary.main" : "background.paper", // Use theme colors
                              color: isAgent ? "primary.contrastText" : "text.primary", // Use theme colors
                              transition: "all 0.2s",
                              wordBreak: "break-word", // Ensure long words wrap
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: 2,
                              },
                            }}
                          >
                            <Typography variant="body1" component="span">
                              {message.content}
                            </Typography>
                          </Paper>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              textAlign: isAgent ? "right" : "left",
                              opacity: message.status === "sending" ? 0.6 : 1, // Visual cue for sending messages
                              fontStyle: message.status === "sending" ? "italic" : "normal", // Optional: italicize sending messages
                            }}
                            component="span" // Changed to span
                          >
                            {formatTime(message.timestamp)}
                            {(() => {
                              const messageDate = new Date(message.timestamp)
                              const today = new Date()
                              if (
                                messageDate.getFullYear() !== today.getFullYear() ||
                                messageDate.getMonth() !== today.getMonth() ||
                                messageDate.getDate() !== today.getDate()
                              ) {
                                return ` - ${formatDate(message.timestamp)}`
                              }
                              return null
                            })()}
                          </Typography>
                        </Box>
                      )
                    })
                  } else {
                    // console.log(
                    //   "[RENDER_DEBUG] No messages to display for",
                    //   currentActiveConvId,
                    //   ". Showing 'No messages yet' prompt.",
                    // )
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "text.secondary",
                        }}
                      >
                        <MessageSquare size={48} color={theme.palette.grey[700]} style={{ marginBottom: 8 }} />
                        <Typography variant="body1">No messages yet</Typography>
                        <Typography variant="body2">Start the conversation</Typography>
                      </Box>
                    )
                  }
                })()
              )}

              {/* Typing Indicator - FINAL POSITION ABOVE INPUT */}
              {activeConversation && isTyping[activeConversation.id]?.isTyping && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 80, // Adjust this value based on the input field's height + desired padding
                    left: theme.spacing(2),
                    zIndex: 1201, // Ensure it's above other elements
                    width: 'auto', // Allow content to dictate width
                    pointerEvents: 'none', // Allow clicks to pass through
                    // Styles for the paper itself
                    borderRadius: 3,
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                    animation: "slideInUp 0.3s ease-out forwards",
                    position: "relative", // For the triangle pseudo-element
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 16,
                      bottom: -8,
                      width: 0,
                      height: 0,
                      borderLeft: "8px solid transparent",
                      borderRight: "8px solid transparent",
                      borderTop: `8px solid ${alpha(theme.palette.background.paper, 0.95)}`,
                    },
                  }}
                >
                  <TypingIndicator name={isTyping[activeConversation.id]?.name || "Guest"} />
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: "divider",
                position: "sticky",
                bottom: 0,
                zIndex: 10,
                bgcolor: "background.paper", // Use theme background
              }}
            >
              <Box
                component="form"
                onSubmit={handleSendMessage}
                sx={{ display: "flex", gap: 1, alignItems: "center", position: "relative" }}
              >
                <TextField
                  fullWidth
                  placeholder="Type a message..."
                  value={messageInput}
                  inputRef={messageInputRef}
                  variant="outlined"
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      transition: "all 0.2s",
                      bgcolor: "background.default", // Use theme background
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}`,
                      },
                    },
                  }}
                  onChange={(e) => {
                    setMessageInput(e.target.value)
                    if (socket && activeConversationRef.current) {
                      // Use ref here
                      if (!isAgentCurrentlyTyping) {
                        sendAgentTypingEvent(true)
                        setIsAgentCurrentlyTyping(true)
                      }
                      const currentConvId = activeConversationRef.current.id // Use ref here
                      if (typingTimeoutRef.current[currentConvId]) {
                        clearTimeout(typingTimeoutRef.current[currentConvId])
                      }
                      typingTimeoutRef.current[currentConvId] = setTimeout(() => {
                        sendAgentTypingEvent(false)
                        setIsAgentCurrentlyTyping(false)
                        typingTimeoutRef.current[currentConvId] = null
                      }, 2000) // Stop typing after 2s of inactivity
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault()
                      handleSendMessage(e)
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <Tooltip title="Add emoji">
                        <IconButton
                          edge="end"
                          onClick={toggleEmojiPicker}
                          sx={{ color: showEmojiPicker ? "primary.main" : "text.secondary" }}
                        >
                          <Smile size={20} />
                        </IconButton>
                      </Tooltip>
                    ),
                  }}
                />
                <IconButton
                  color="primary"
                  disabled={!messageInput.trim()}
                  type="submit"
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    marginBottom: "10px",
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.3),
                    },
                    transition: "all 0.2s",
                    transform: messageInput.trim() ? "scale(1)" : "scale(0.95)",
                    opacity: messageInput.trim() ? 1 : 0.7,
                    boxShadow: 1,
                  }}
                >
                  <Send size={20} />
                </IconButton>
                {/* Emoji Picker */}
                {showEmojiPicker && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "100%",
                      right: 0,
                      mb: 1,
                      zIndex: 1000,
                      animation: "fadeIn 0.2s ease-out",
                      boxShadow: 3,
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <EmojiPicker
                      onEmojiClick={addEmoji}
                      width={320}
                      height={400}
                      theme={theme.palette.mode} // Use theme mode for emoji picker
                      previewConfig={{ showPreview: false }}
                    />
                  </Box>
                )}
                {/* Quick Emoji Access */}
                <Box
                  sx={{
                    position: "absolute",
                    bottom: "100%",
                    left: 0,
                    mb: 1,
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    maxWidth: "80%",
                  }}
                >
                  {showEmojiPicker &&
                    commonEmojis.map((emoji, index) => (
                      <Button
                        key={index}
                        variant="text"
                        size="small"
                        onClick={() => addEmoji({ emoji })}
                        sx={{
                          minWidth: "auto",
                          p: 0.5,
                          borderRadius: 1,
                          fontSize: "1.2rem",
                          lineHeight: 1,
                          bgcolor: "background.paper", // Use theme background
                          boxShadow: 1,
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            transform: "scale(1.1)",
                          },
                          transition: "all 0.2s",
                          animation: "fadeIn 0.3s ease-out forwards",
                          animationDelay: `${index * 0.03}s`,
                        }}
                      >
                        {emoji}
                      </Button>
                    ))}
                </Box>
              </Box>
            </Paper>
          </>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              bgcolor: "background.default", // Use theme background
            }}
          >
            <MessageSquare size={64} color={theme.palette.grey[700]} style={{ marginBottom: 16 }} />
            <Typography variant="h5" sx={{ mb: 1, fontWeight: 500, color: "text.primary" }}>
              No conversation selected
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: "md", textAlign: "center" }}>
              Select a conversation from the sidebar to start chatting or wait for new incoming messages
            </Typography>
          </Box>
        )}
      </Box>

      {/* Visitor Details Panel */}
      {activeConversation && (
        <Box
          sx={{
            width: { xs: "100%", md: 320 },
            flexShrink: 0,
            borderLeft: { md: 1 },
            borderColor: { md: "divider" },
            bgcolor: "background.paper", // Use theme background
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden",
            position: { xs: "fixed", md: "relative" },
            right: { xs: 0, md: "auto" },
            zIndex: { xs: 1100, md: 1 },
            boxShadow: { xs: "-4px 0 10px rgba(0, 0, 0, 0.3)", md: "none" },
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontWeight: 600,
                color: theme.palette.primary.main,
                animation: "fadeIn 0.5s ease-out",
              }}
            >
              <Info size={20} /> Visitor Details
            </Typography>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              p: 0,
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: alpha(theme.palette.primary.main, 0.2),
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: alpha(theme.palette.primary.main, 0.4),
              },
            }}
          >
            {isVisitorDetailsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress size={40} thickness={4} />
              </Box>
            ) : currentVisitorDetails ? (
              <>
                {/* Visitor Summary Card */}
                <Paper
                  elevation={0}
                  sx={{
                    m: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    animation: "fadeIn 0.5s ease-out",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                        color: theme.palette.primary.main,
                        mr: 2,
                      }}
                    >
                      {(activeConversation?.guest || "G").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {activeConversation?.guest || "Guest"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: "text.secondary",
                          gap: 0.5,
                        }}
                      >
                        <Globe size={14} />
                        {(() => {
                          if (!currentVisitorDetails?.location) {
                            return "Location unknown"
                          }
                          const locationString = [
                            currentVisitorDetails.location.city,
                            currentVisitorDetails.location.country,
                          ]
                            .filter(Boolean)
                            .join(", ")
                          return locationString || "Location unknown"
                        })()}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mt: 2,
                      pt: 2,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        Browser
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {currentVisitorDetails.browser?.split(" ")[0] || "Unknown"}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        OS
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {currentVisitorDetails.os?.split(" ")[0] || "Unknown"}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        Screen
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {currentVisitorDetails.screenWidth && currentVisitorDetails.screenHeight
                          ? `${currentVisitorDetails.screenWidth}×${currentVisitorDetails.screenHeight}`
                          : "Unknown"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                {/* Device Information */}
                <Paper
                  elevation={0}
                  sx={{
                    m: 2,
                    p: 0,
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    animation: "fadeIn 0.7s ease-out",
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      <Monitor size={16} /> Device Information
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <Monitor size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.browser || "N/A"}
                          </Typography>
                        }
                        secondary="Browser"
                      />
                    </ListItem>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <HardDrive size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.os || "N/A"}
                          </Typography>
                        }
                        secondary="Operating System"
                      />
                    </ListItem>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <Type size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.language || "N/A"}
                          </Typography>
                        }
                        secondary="Language"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <PanelRight size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.screenWidth && currentVisitorDetails.screenHeight
                              ? `${currentVisitorDetails.screenWidth}×${currentVisitorDetails.screenHeight}`
                              : "N/A"}
                          </Typography>
                        }
                        secondary="Screen Resolution"
                      />
                    </ListItem>
                  </List>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    m: 2,
                    p: 0,
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    animation: "fadeIn 0.7s ease-out",
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      <User size={16} /> User Info
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <Mail size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.email || "N/A"}
                          </Typography>
                        }
                        secondary="Email"
                      />
                    </ListItem>
                  </List>
                </Paper>
                {/* Agent Selection Section */}
                {/* console.log("[TEAM_MGMT_DEBUG] User ID:", user?._id) */}
                {/* console.log("[TEAM_MGMT_DEBUG] Org Owner ID:", orgFromHook?.owner) */}
                {/* console.log("[TEAM_MGMT_DEBUG] IDs Match?:", user?._id === orgFromHook?.owner) */}
                {/* console.log(
                  "[TEAM_MGMT_DEBUG] Full Condition:",
                  user?._id && orgFromHook?.owner && user._id === orgFromHook.owner,
                ) */}
                {user?._id && orgFromHook?.owner && user._id === orgFromHook.owner && (
                  <Paper
                    elevation={0}
                    sx={{
                      m: 2,
                      p: 0,
                      borderRadius: 3,
                      overflow: "hidden",
                      bgcolor: alpha(theme.palette.background.paper, 0.8),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      animation: "fadeIn 0.7s ease-out",
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.1)} 100%)`,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                        position: "relative",
                        overflow: "hidden",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `radial-gradient(circle at 20% 50%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%)`,
                          pointerEvents: "none",
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          fontSize: "1.1rem",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <UserPlus size={18} />
                        </Box>
                        Team Management
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mt: 0.5,
                          position: "relative",
                          zIndex: 1,
                          opacity: 0.9,
                        }}
                      >
                        Assign agents or invite new team members
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      {/* Available Agents Section */}
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "text.primary",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: theme.palette.secondary?.main || theme.palette.primary.main,
                              animation: "pulse 2s infinite",
                            }}
                          />
                          Available Agents ({availableAgents.length})
                        </Typography>
                        <Box
                          sx={{
                            maxHeight: 280,
                            overflowY: "auto",
                            pr: 1,
                            "&::-webkit-scrollbar": {
                              width: "4px",
                            },
                            "&::-webkit-scrollbar-track": {
                              background: "transparent",
                            },
                            "&::-webkit-scrollbar-thumb": {
                              background: alpha(theme.palette.primary.main, 0.3),
                              borderRadius: "2px",
                            },
                          }}
                        >
                          {availableAgents.map((agent, index) => (
                            <Box
                              key={agent.id}
                              onClick={() => handleAgentSelection(agent)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 1.5,
                                borderRadius: 2.5,
                                cursor: "pointer",
                                mb: 1.5,
                                position: "relative",
                                overflow: "hidden",
                                bgcolor:
                                  selectedAgent?.id === agent.id
                                    ? alpha(theme.palette.primary.main, 0.12)
                                    : alpha(theme.palette.background.paper, 0.5),
                                border: `1.5px solid ${
                                  selectedAgent?.id === agent.id
                                    ? alpha(theme.palette.primary.main, 0.4)
                                    : alpha(theme.palette.divider, 0.3)
                                }`,
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                transform: selectedAgent?.id === agent.id ? "scale(1.02)" : "scale(1)",
                                animation: `fadeIn 0.5s ease-out ${index * 0.1}s both`,
                                "&:hover": {
                                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  transform: "scale(1.02) translateY(-2px)",
                                  boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                },
                                "&::before":
                                  selectedAgent?.id === agent.id
                                    ? {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.03)} 100%)`,
                                        pointerEvents: "none",
                                      }
                                    : {},
                              }}
                            >
                              {/* Avatar and Info */}
                              <Box sx={{ position: "relative" }}>
                                <Avatar
                                  sx={{
                                    width: 44,
                                    height: 44,
                                    mr: 2,
                                    bgcolor:
                                      selectedAgent?.id === agent.id
                                        ? theme.palette.primary.main
                                        : onlineAgentIds.has(agent.id)
                                          ? alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.25)
                                          : alpha(theme.palette.grey[700], 0.2),
                                    color:
                                      selectedAgent?.id === agent.id
                                        ? "white"
                                        : onlineAgentIds.has(agent.id)
                                          ? theme.palette.secondary?.main || theme.palette.primary.main
                                          : theme.palette.grey[500],
                                    fontWeight: 600,
                                    fontSize: "1.1rem",
                                    transition: "all 0.3s ease",
                                    boxShadow:
                                      selectedAgent?.id === agent.id
                                        ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                                        : "none",
                                  }}
                                >
                                  {agent.name.charAt(0)}
                                </Avatar>
                              </Box>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    color: "text.primary",
                                    mb: 0.2,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {agent.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <Mail size={12} />
                                  {agent.email}
                                </Typography>
                                {/* Status Label */}
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: onlineAgentIds.has(agent.id) ? "success.main" : "text.secondary",
                                      fontWeight: 500,
                                      letterSpacing: 0.2,
                                    }}
                                  >
                                    {onlineAgentIds.has(agent.id) ? "Online" : "Offline"}
                                  </Typography>
                                </Box>
                              </Box>
                              {selectedAgent?.id === agent.id && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 32,
                                    height: 32,
                                    marginBottom: "22px",
                                    borderRadius: "50%",
                                    animation: "scaleIn 0.3s ease-out",
                                  }}
                                >
                                  <CheckCircle size={18} style={{ color: theme.palette.primary.main }} />
                                </Box>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      {/* Divider */}
                      <Box
                        sx={{
                          height: 1,
                          bgcolor: alpha(theme.palette.divider, 0.3),
                          mb: 3,
                          background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.divider, 0.3)} 50%, transparent 100%)`,
                        }}
                      />
                      {/* Invite New Agent Section */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: "text.primary",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              bgcolor: theme.palette.primary.main,
                              animation: "pulse 2s infinite",
                            }}
                          />
                          Expand Your Team
                        </Typography>
                        <Box
                          sx={{
                            p: 2.5,
                            borderRadius: 2.5,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary?.main || theme.palette.primary.main, 0.05)} 100%)`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            position: "relative",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: -50,
                              right: -50,
                              width: 100,
                              height: 100,
                              borderRadius: "50%",
                              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
                              pointerEvents: "none",
                            },
                          }}
                        >
                          <Box sx={{ position: "relative", zIndex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                mb: 2,
                                lineHeight: 1.5,
                              }}
                            >
                              Need more hands on deck? Invite new agents to join your support team and provide better
                              customer service.
                            </Typography>
                            <Button
                              variant="contained"
                              size="large"
                              startIcon={<UserPlus size={20} />}
                              onClick={handleOpenInviteModal}
                              disabled={!orgFromHook || !orgFromHook._id}
                              sx={{
                                width: "100%",
                                py: 1.5,
                                borderRadius: 2,
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                                boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${theme.palette.primary.main} 100%)`,
                                },
                                "&:disabled": {
                                  background: alpha(theme.palette.grey[500], 0.3),
                                  color: alpha(theme.palette.text.primary, 0.5),
                                  transform: "none",
                                  boxShadow: "none",
                                },
                              }}
                            >
                              Invite New Agent
                            </Button>
                            {(!orgFromHook || !orgFromHook._id) && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: "block",
                                  mt: 1,
                                  color: alpha(theme.palette.error?.main || theme.palette.primary.main, 0.8),
                                  textAlign: "center",
                                  fontStyle: "italic",
                                }}
                              >
                                Organization loading...
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                )}
                {/* Session Information */}
                <Paper
                  elevation={0}
                  sx={{
                    m: 2,
                    p: 0,
                    borderRadius: 2,
                    overflow: "hidden",
                    bgcolor: alpha(theme.palette.background.paper, 0.5),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    animation: "fadeIn 0.8s ease-out",
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                      }}
                    >
                      <Activity size={16} /> Session Information
                    </Typography>
                  </Box>
                  <List dense disablePadding>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <Globe size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Tooltip title={currentVisitorDetails.ipAddress || "N/A"}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {currentVisitorDetails.ipAddress || "N/A"}
                            </Typography>
                          </Tooltip>
                        }
                        secondary="IP Address"
                      />
                    </ListItem>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <Link size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Tooltip title={currentVisitorDetails.currentPage || "N/A"}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {currentVisitorDetails.currentPage || "N/A"}
                            </Typography>
                          </Tooltip>
                        }
                        secondary="Current Page"
                      />
                    </ListItem>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <Calendar size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.connectedAt
                              ? `${formatDate(currentVisitorDetails.connectedAt)} ${formatTime(currentVisitorDetails.connectedAt)}`
                              : "N/A"}
                          </Typography>
                        }
                        secondary="Connected At"
                      />
                    </ListItem>
                    <ListItem divider>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <LogIn size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Tooltip title={currentVisitorDetails.socketId || "N/A"}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {currentVisitorDetails.socketId || "N/A"}
                            </Typography>
                          </Tooltip>
                        }
                        secondary="Socket ID"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                          }}
                        >
                          <User size={14} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {currentVisitorDetails.widgetId || "N/A"}
                          </Typography>
                        }
                        secondary="Widget ID"
                      />
                    </ListItem>
                  </List>
                </Paper>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: alpha(theme.palette.error?.main || theme.palette.primary.main, 0.1),
                    color: theme.palette.error?.main || theme.palette.primary.main,
                    mb: 2,
                  }}
                >
                  <WifiOff size={32} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, color: "text.primary" }}>
                  No Visitor Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Visitor details are not available for this conversation. This could be due to privacy settings or
                  connection issues.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Invite Agent Modal/Dialog */}
      <Dialog open={isInviteModalOpen} onClose={handleCloseInviteModal} maxWidth="sm" fullWidth>
        <DialogTitle>Invite New Agent</DialogTitle>
        <DialogContent>
          <InviteAgentForm onSuccess={handleCloseInviteModal} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInviteModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    
      <style jsx global>{`
      /* Enhanced animations for the typing indicator */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes typingBounce {
  0%,
  100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes slideInRight {
  from {
    transform: translateX(20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

  
  `}</style> 
    
    </Box>
  )
}

export default AgentDashboard



















      {/* Global styles */}




